import { COST_COMPONENT_ORDER } from 'src/constants/componentConstants/costModelling'
import { v4 as uuidv4 } from 'uuid'

const capitalizeFirstLetter = (str) => {
  return str.replace(/\b\w/g, function (char) {
    return char.toUpperCase()
  })
}

export const TransformComponentData = (data) => {
  const componentKeysMap = {
    'raw material cost': 'raw_materials',
    'packaging cost': 'packaging_cost',
    'labor cost': 'labor_cost',
    'Freight cost': 'freight_cost',
  }

  const validKeys = [
    'overhead cost',
    'raw material cost',
    'processing cost',
    'labor cost',
    'packaging cost',
    'profit margin',
    'Freight cost',
    'tariff rate',
  ]

  const componentNameKeyMap = {
    'raw material cost': 'Raw Material & Scrap',
    'processing cost': 'Conversion',
    'labor cost': 'Labor',
    'packaging cost': 'Packaging',
    'overhead cost': 'Overhead & Margin',
    'profit margin': 'Overhead & Margin',
    'Freight cost': 'Logistics',
    'tariff rate': 'Duty & Tariff',
  }

  // Track added labels to avoid duplicates
  const addedLabels = new Map()

  // Ensure the necessary keys are defined before processing
  if (!data || !data.break_down || !data.break_down_details) {
    return []
  }

  Object.keys(data.break_down).forEach((key) => {
    if (validKeys.includes(key) && data?.break_down[key] !== 0) {
      const label = componentNameKeyMap[key] || key
      const percentage = parseFloat(data.break_down[key]) || 0
      let detailData = []

      if (componentKeysMap.hasOwnProperty(key)) {
        const mappedKey = componentKeysMap[key]
        const detail = data.break_down_details[mappedKey] || {}
        detailData = Object.keys(detail).map((detailKey) => ({
          label: capitalizeFirstLetter(detailKey),
          percentage: detail[detailKey].hasOwnProperty('percent')
            ? detail[detailKey].percent
            : typeof detail[detailKey] === 'string'
            ? detail[detailKey].replace('%', '')
            : detail[detailKey],
          indexTicker: detail[detailKey].hasOwnProperty('indexTicker')
            ? detail[detailKey].indexTicker === 'NULL' || detail[detailKey].indexTicker === 'null'
              ? ''
              : detail[detailKey].indexTicker
            : '',
          indexId: uuidv4(),
        }))
      }

      // Check if the label has already been added
      if (addedLabels.has(label)) {
        // Update the percentage if the label already exists
        const existingEntry = addedLabels.get(label)
        existingEntry.percentage += percentage
        existingEntry.data.push(...detailData) // Add new details to existing entry
      } else {
        // Add a new entry for the label
        addedLabels.set(label, {
          label,
          percentage,
          data: detailData,
        })
      }
    }
  })

  // Convert the added labels map to an array and sort based on COST_COMPONENT_ORDER
  if (!Array.isArray(COST_COMPONENT_ORDER)) {
    return [] // Return empty array if COST_COMPONENT_ORDER is not defined
  }

  // Convert the added labels map to an array and sort based on COST_COMPONENT_ORDER
  const sortedData = Array.from(addedLabels.values())?.sort(
    (a, b) => COST_COMPONENT_ORDER.indexOf(a.label) - COST_COMPONENT_ORDER.indexOf(b.label),
  )

  return sortedData
}

export function transformData(data) {
  // Check if the data is in transformed format by checking for the 'subcomponent_name' key
  const isTransformed = data?.some((component) => component.sub_components)

  return data?.map((component, componentIndex) => {
    if (isTransformed) {
      // Reverse transform subcomponents
      const subComponents = component.sub_components.map((subcomponent) => ({
        label: subcomponent.subcomponent_name,
        percentage: subcomponent.subcomponent_percentage,
        indexTicker: subcomponent.subcomponent_index_ticker,
        indexId: uuidv4(),
      }))

      // Check for cost_driver_id and assign it as costDriverId if present
      const componentData = {
        label: component.component_name,
        sectionId: component.section_id,
        percentage: component.component_percentage,
        data: subComponents,
      }

      if (component.cost_driver_id) {
        componentData.costDriverId = component.cost_driver_id // Assigning costDriverId if present
      }

      // Return the reversed component object
      return componentData
    } else {
      // Transform subcomponents
      const subComponents = component.data.map((subcomponent, subIndex) => ({
        subcomponent_name: subcomponent.label,
        subcomponent_percentage: subcomponent.percentage,
        subcomponent_index_ticker: subcomponent.indexTicker,
        subcomponent_order_id: subIndex + 1, // Subcomponent order starts from 1
      }))

      // Check for costDriverId and assign it as cost_driver_id if present
      const componentData = {
        component_name: component.label,
        section_id: component.sectionId,
        component_percentage: component.percentage,
        sub_components: subComponents,
        order_id: componentIndex + 1, // Component order starts from 1
      }

      if (component.costDriverId) {
        componentData.cost_driver_id = component.costDriverId // Assigning cost_driver_id if present
      }

      // Return the transformed component object
      return componentData
    }
  })
}
